/*
 * Copyright 2025 ixPilot Teknoloji A.Ş. (ixpilot2021@gmail.com)
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, modification, or distribution of this software,
 * via any medium, is strictly prohibited.
 *
 * Developed and maintained by ixPilot Teknoloji A.Ş.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Flex,
  HStack,
  Input,
  Select,
  Button,
  Text,
  Spinner,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Textarea,
  FormHelperText,
  useDisclosure,
} from "@chakra-ui/react";
import { FiPlus, FiRefreshCw } from "react-icons/fi";
import SatisSonrasiContextMenu from "components/ContextMenu/SatisSonrasiContextMenu";
import api from "utils/api";
import { useErrorHandler } from "hooks/useErrorHandler";
import { formatFirmaName } from "utils/formatFirmaName";

export default function SatisSonrasi() {
  const toast = useToast();
  const { handleError } = useErrorHandler(); // Merkezi error handling
  const [searchText, setSearchText] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [durumFilter, setDurumFilter] = useState("beklemede");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const createModal = useDisclosure();
  const [saving, setSaving] = useState(false);
  const [devices, setDevices] = useState([]);
  const [devicesLoading, setDevicesLoading] = useState(false);
  const [devicesError, setDevicesError] = useState(null);
  const [newTicket, setNewTicket] = useState({
    device_id: "",
    konu: "",
    aciklama: "",
    oncelik: "normal",
    atanan_kisi: "",
  });
  const [contextMenuPos, setContextMenuPos] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [resolveModal, setResolveModal] = useState({
    isOpen: false,
    row: null,
    telefon: "",
    sms_message: "",
    note: "",
  });
  const [smsModal, setSmsModal] = useState({
    isOpen: false,
    row: null,
    telefon: "",
    message: "",
  });
  const [noteModal, setNoteModal] = useState({
    isOpen: false,
    row: null,
    not_metni: "",
  });

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(searchText), 400);
    return () => clearTimeout(handler);
  }, [searchText]);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (debouncedSearch) params.append("q", debouncedSearch);
      if (durumFilter && durumFilter !== "tum") params.append("durum", durumFilter);

      const query = params.toString();
      const res = await api.get(`/api/crm/satis-sonrasi${query ? `?${query}` : ""}`);
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Veriler alınamadı.");
      }
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      const errorMessage = handleError(err, "Satış sonrası verileri yükleme hatası");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, durumFilter, handleError]);

  const fetchDevices = useCallback(async () => {
    setDevicesLoading(true);
    setDevicesError(null);
    try {
      const res = await api.get("/api/devices");
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Araç listesi alınamadı.");
      }
      setDevices(Array.isArray(data) ? data : []);
    } catch (err) {
      setDevicesError(err.message);
    } finally {
      setDevicesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
    fetchDevices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchItems]);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("tr-TR");
  };

  const formatDurumLabel = (value) => {
    if (!value) return "-";
    switch (value) {
      case "beklemede":
        return "Beklemede";
      case "devam_ediyor":
        return "Devam Ediyor";
      case "cozuldu":
        return "Çözüldü";
      case "iptal":
        return "İptal";
      default:
        return value;
    }
  };

  const formatOncelikLabel = (value) => {
    if (!value) return "-";
    switch (value) {
      case "dusuk":
        return "Düşük";
      case "normal":
        return "Normal";
      case "yuksek":
        return "Yüksek";
      default:
        return value;
    }
  };

  const tableData = useMemo(() => {
    return items.map((item) => ({
      id: item.id,
      firma_adi: formatFirmaName(item.firma_adi || ""),
      plaka: item.plaka || "-",
      telefon: item.telefon || "",
      konu: item.konu || "-",
      durum: item.durum || "",
      durum_label: formatDurumLabel(item.durum),
      oncelik: item.oncelik || "",
      oncelik_label: formatOncelikLabel(item.oncelik),
      atanan_kisi: item.atanan_kisi || "",
      cozum_tarihi: formatDate(item.cozum_tarihi),
      created_at: formatDate(item.created_at),
      raw: item,
    }));
  }, [items]);

  const handleRowContextMenu = (event, row) => {
    event.preventDefault();
    setSelectedRow(row);
    setContextMenuPos({ x: event.clientX, y: event.clientY });
  };

  const closeContextMenu = () => {
    setContextMenuPos(null);
    setSelectedRow(null);
  };

  // eslint-disable-next-line no-unused-vars
  const openResolveModal = async (row) => {
    // Device bilgisini çek ve firma telefonunu bul
    let telefon = row?.telefon || row?.raw?.telefon || "";
    
    // Eğer kayıt üzerinde telefon yoksa, device üzerinden firma telefonunu bul
    if (!telefon && row?.raw?.device_id) {
      try {
        // Önce devices listesinde ara
        const device = devices.find((d) => String(d.id) === String(row.raw.device_id));
        if (device && device.firma_telefon) {
          telefon = device.firma_telefon;
        } else {
          // Devices listesinde yoksa API'den çek
          const res = await api.get("/api/devices");
          const data = await res.json();
          if (res.ok && Array.isArray(data)) {
            const foundDevice = data.find((d) => String(d.id) === String(row.raw.device_id));
            if (foundDevice && foundDevice.firma_telefon) {
              telefon = foundDevice.firma_telefon;
            }
          }
        }
      } catch (err) {
        // Hata sessizce yok sayılıyor
      }
    }
    
    setResolveModal({
      isOpen: true,
      row,
      telefon,
      sms_message: "",
      note: "",
    });
  };

  const closeResolveModal = () => {
    setResolveModal({
      isOpen: false,
      row: null,
      telefon: "",
      sms_message: "",
      note: "",
    });
  };

  const handleResolveSave = async () => {
    const { row, telefon, sms_message, note } = resolveModal;
    if (!row) {
      closeResolveModal();
      return;
    }
    
    // Telefon numarası validasyonu
    if (sms_message && telefon) {
      const phoneRegex = /^0\d{10}$/;
      if (!phoneRegex.test(telefon.trim())) {
        toast({
          title: "Geçersiz telefon numarası",
          description: "Telefon numarası 0 ile başlayan 11 haneli olmalıdır (örn: 05321234567)",
          status: "warning",
        });
        return;
      }
    }
    
    try {
      const payload = {
        telefon: telefon && telefon.trim() ? telefon.trim() : null,
        sms_message: sms_message || null,
        not_metni: note || null,
        cozum_tarihi: null,
      };
      const res = await api.post(
        `/api/crm/satis-sonrasi/${row.id}/resolve`,
        payload
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Kayıt çözüldü olarak işaretlenemedi.");
      }
      toast({
        title: "Kayıt çözüldü olarak işaretlendi",
        status: "success",
      });
      closeResolveModal();
      fetchItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Kayıt çözümleme hatası");
    }
  };

  const openSmsModal = async (row) => {
    let telefon = row?.telefon || row?.raw?.telefon || "";
    let musteriAdi = "";
    
    // Önce kayıt üzerindeki bilgileri kontrol et
    const raw = row?.raw || {};
    musteriAdi = raw.yetkili_kisi || raw.firma_adi || "";
    
    // Device bilgisini çek ve firma bilgilerini al
    if (row?.raw?.device_id) {
      try {
        let device = devices.find((d) => String(d.id) === String(row.raw.device_id));
        
        if (!device) {
          // Devices listesinde yoksa API'den çek
          const res = await api.get("/api/devices");
          const data = await res.json();
          if (res.ok && Array.isArray(data)) {
            device = data.find((d) => String(d.id) === String(row.raw.device_id));
          }
        }
        
        if (device) {
          // Telefon numarasını al
          if (!telefon && device.firma_telefon) {
            telefon = device.firma_telefon;
          }
          
          // Müşteri adını al (device üzerinden firma yetkili adı - öncelikli)
          if (device.firma_yetkili_adi && device.firma_yetkili_adi.trim()) {
            musteriAdi = device.firma_yetkili_adi.trim();
          } else if (!musteriAdi && device.firma_ismi && device.firma_ismi.trim()) {
            // Yetkili adı yoksa firma ismini kullan
            musteriAdi = device.firma_ismi.trim();
          }
        }
      } catch (err) {
        // Hata sessizce yok sayılıyor
      }
    }
    
    // Varsayılan mesajı oluştur
    const firmaAdi = musteriAdi.trim() || "Müşterimiz";
    const tarih = raw.created_at 
      ? new Date(raw.created_at).toLocaleDateString("tr-TR", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : new Date().toLocaleDateString("tr-TR", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
    
    const defaultMessage = `Sayın Müşterimiz, ${firmaAdi} adına ${tarih} tarihinde açılan destek kaydınız başarıyla çözümlenmiştir.`;
    
    setSmsModal({
      isOpen: true,
      row,
      telefon,
      message: defaultMessage,
    });
  };

  const closeSmsModal = () => {
    setSmsModal({
      isOpen: false,
      row: null,
      telefon: "",
      message: "",
    });
  };

  const handleSmsSave = async () => {
    const { row, telefon, message } = smsModal;
    if (!row) {
      closeSmsModal();
      return;
    }
    
    if (!message.trim()) {
      toast({
        title: "SMS mesajı gerekli",
        status: "warning",
      });
      return;
    }
    
    if (telefon) {
      const phoneRegex = /^0\d{10}$/;
      if (!phoneRegex.test(telefon.trim())) {
        toast({
          title: "Geçersiz telefon numarası",
          description: "Telefon numarası 0 ile başlayan 11 haneli olmalıdır (örn: 05321234567)",
          status: "warning",
        });
        return;
      }
    }
    
    try {
      const payload = {
        telefon: telefon && telefon.trim() ? telefon.trim() : null,
        message: message.trim(),
      };
      const res = await api.post(
        `/api/crm/satis-sonrasi/${row.id}/sms`,
        payload
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "SMS gönderilemedi.");
      }
      toast({
        title: "SMS başarıyla gönderildi",
        status: "success",
      });
      closeSmsModal();
      fetchItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Kayıt çözümleme hatası");
    }
  };

  const openNoteModal = (row) => {
    setNoteModal({
      isOpen: true,
      row,
      not_metni: "",
    });
  };

  const closeNoteModal = () => {
    setNoteModal({
      isOpen: false,
      row: null,
      not_metni: "",
    });
  };

  const handleNoteSave = async () => {
    const { row, not_metni } = noteModal;
    if (!row) {
      closeNoteModal();
      return;
    }
    
    if (!not_metni.trim()) {
      toast({
        title: "Not metni gerekli",
        status: "warning",
      });
      return;
    }
    
    try {
      const payload = {
        not_metni: not_metni.trim(),
      };
      const res = await api.post(
        `/api/crm/satis-sonrasi/${row.id}/note`,
        payload
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Not eklenemedi.");
      }
      toast({
        title: "Not başarıyla eklendi",
        status: "success",
      });
      closeNoteModal();
      fetchItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Kayıt çözümleme hatası");
    }
  };

  const handleResolveOnly = async (row) => {
    if (!row) return;
    try {
      const res = await api.post(
        `/api/crm/satis-sonrasi/${row.id}/resolve`,
        {
          telefon: null,
          sms_message: null,
          not_metni: null,
          cozum_tarihi: null,
        }
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data?.detail || "Kayıt çözüldü olarak işaretlenemedi.");
      }
      toast({
        title: "Kayıt çözüldü olarak işaretlendi",
        status: "success",
      });
      fetchItems();
    } catch (err) {
      // Merkezi error handling - Güvenli mesaj göster
      handleError(err, "Kayıt çözümleme hatası");
    }
  };

  const filterControls = (
    <HStack spacing={3} flexWrap="wrap">
      <Input
        size="sm"
        placeholder="Ara..."
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        bg="white"
        width={{ base: "100%", md: "220px" }}
        fontSize="12px"
        borderRadius="md"
        border="1px solid"
        borderColor="gray.200"
        _hover={{ borderColor: "gray.300" }}
        _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
      />
      <Select
        size="sm"
        value={durumFilter}
        onChange={(e) => setDurumFilter(e.target.value)}
        width={{ base: "100%", md: "160px" }}
        bg="white"
        borderColor="gray.200"
        color="gray.700"
        fontSize="12px"
        borderRadius="md"
        _hover={{ borderColor: "gray.300" }}
        _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
      >
        <option value="tum">Tümü</option>
        <option value="beklemede">Beklemede</option>
        <option value="devam_ediyor">Devam Ediyor</option>
        <option value="cozuldu">Çözüldü</option>
        <option value="iptal">İptal</option>
      </Select>
      <Button
        size="sm"
        variant="outline"
        bg="white"
        color="gray.700"
        borderColor="gray.300"
        leftIcon={<FiRefreshCw />}
        fontSize="12px"
        fontWeight="medium"
        _hover={{ bg: "gray.50", borderColor: "gray.400" }}
        _active={{ bg: "gray.100" }}
        onClick={fetchItems}
      >
        Yenile
      </Button>
      <Button
        size="sm"
        bg="white"
        color="gray.700"
        borderColor="gray.300"
        border="1px solid"
        leftIcon={<FiPlus />}
        fontSize="12px"
        fontWeight="600"
        borderRadius="md"
        boxShadow="sm"
        _hover={{ 
          transform: "translateY(-1px)", 
          boxShadow: "md",
          bg: "gray.50",
          borderColor: "gray.400"
        }}
        _active={{ transform: "translateY(0)", bg: "gray.100" }}
        onClick={() => {
          if (!devices.length && !devicesLoading) {
            fetchDevices();
          }
          createModal.onOpen();
        }}
      >
        Yeni Ekle
      </Button>
    </HStack>
  );

  return (
    <Box mt={12}>
      <Flex justify="space-between" align="center" mb={4}>
        <Text fontSize="lg" fontWeight="bold" color="white">
          Satış Sonrası Destek
        </Text>
        {filterControls}
      </Flex>

      {error && (
        <Box bg="red.50" border="1px solid" borderColor="red.100" p={3} borderRadius="md" mb={3}>
          <Text fontSize="sm" color="red.600">
            {error}
          </Text>
        </Box>
      )}
      {loading && (
        <Flex justify="center" align="center" py={6}>
          <Spinner />
        </Flex>
      )}

      {/* Kart bazlı destek kayıtları */}
      <Flex direction="column" gap={3}>
        {tableData.map((row) => {
          const isExpanded = expandedId === row.id;
          const raw = row.raw || {};
          const hasFirma = row.firma_adi && row.firma_adi.trim().length > 0;
          const hasTelefon = row.telefon && row.telefon.trim().length > 0;
          const altSatirText =
            hasFirma && hasTelefon
              ? `${row.firma_adi} • ${row.telefon}`
              : hasFirma
              ? row.firma_adi
              : hasTelefon
              ? row.telefon
              : "";
          return (
            <Box
              key={row.id}
              bg="white"
              borderRadius="2xl"
              p={4}
              boxShadow="md"
              border="1px solid"
              borderColor={isExpanded ? "blue.100" : "gray.100"}
              cursor="pointer"
              onClick={() =>
                setExpandedId((prev) => (prev === row.id ? null : row.id))
              }
              onContextMenu={(e) => handleRowContextMenu(e, row)}
            >
              {/* Özet satırı */}
              <Flex justify="space-between" align="center">
                <Box>
                  <Text fontSize="md" fontWeight="bold">
                    {row.plaka} • {row.konu}
                  </Text>
                  {altSatirText && (
                    <Text fontSize="sm" color="gray.600">
                      {altSatirText}
                    </Text>
                  )}
                </Box>
                <HStack spacing={4} align="center">
                  <Box textAlign="right">
                    <Text fontSize="xs" color="gray.500">
                      Durum
                    </Text>
                    <Text fontSize="sm" fontWeight="bold">
                      {row.durum_label}
                    </Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="xs" color="gray.500">
                      Öncelik
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {row.oncelik_label}
                    </Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="xs" color="gray.500">
                      Çözüm Tarihi
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {row.cozum_tarihi}
                    </Text>
                  </Box>
                  <Box textAlign="right">
                    <Text fontSize="xs" color="gray.500">
                      Açılış Tarihi
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {row.created_at}
                    </Text>
                  </Box>
                </HStack>
              </Flex>

              {/* Detay kısmı */}
              {isExpanded && (
                <Box mt={4}>
                  <HStack spacing={2} mb={3}>
                    <Button
                      size="sm"
                      colorScheme="green"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleResolveOnly(row);
                      }}
                    >
                      Çözüldü Olarak İşaretle
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="teal"
                      onClick={(e) => {
                        e.stopPropagation();
                        openSmsModal(row);
                      }}
                    >
                      SMS Gönder
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      colorScheme="blue"
                      onClick={(e) => {
                        e.stopPropagation();
                        openNoteModal(row);
                      }}
                    >
                      Not Ekle
                    </Button>
                  </HStack>

                  <Box mb={3}>
                    <Text fontSize="xs" color="gray.500">
                      Atanan Kişi
                    </Text>
                    <Text fontSize="sm" fontWeight="medium">
                      {row.atanan_kisi || "-"}
                    </Text>
                  </Box>

                  <Box mb={3}>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Açıklama
                    </Text>
                    <Box
                      bg="gray.50"
                      borderRadius="md"
                      p={3}
                      maxH="160px"
                      overflow="auto"
                    >
                      <Text fontSize="sm" whiteSpace="pre-wrap">
                        {raw.aciklama || "Açıklama girilmemiş."}
                      </Text>
                    </Box>
                  </Box>

                  <Box>
                    <Text fontSize="xs" color="gray.500" mb={1}>
                      Çözüm / Not Geçmişi
                    </Text>
                    <Box
                      bg="gray.50"
                      borderRadius="md"
                      p={3}
                      maxH="160px"
                      overflow="auto"
                    >
                      <Text fontSize="sm" whiteSpace="pre-wrap">
                        {raw.notlar || "Henüz çözüm notu girilmemiş."}
                      </Text>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          );
        })}
      </Flex>

      {/* Yeni Destek Kaydı Modalı */}
      <Modal isOpen={createModal.isOpen} onClose={createModal.onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Yeni Destek Kaydı</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <FormControl mb={3} isRequired>
              <FormLabel fontSize="sm">Araç (Plaka)</FormLabel>
              <Select
                size="sm"
                placeholder={
                  devicesLoading ? "Araçlar yükleniyor..." : "Araç seçiniz"
                }
                value={newTicket.device_id}
                onChange={(e) =>
                  setNewTicket((prev) => ({ ...prev, device_id: e.target.value }))
                }
                isDisabled={devicesLoading || !!devicesError}
              >
                {devices.map((d) => (
                  <option key={d.id} value={d.id}>
                    {`${d.plaka} - ${d.firma_adi || ""}`.trim()}
                  </option>
                ))}
              </Select>
              {devicesError && (
                <FormHelperText fontSize="xs" color="red.400">
                  {devicesError}
                </FormHelperText>
              )}
            </FormControl>

            <FormControl mb={3} isRequired>
              <FormLabel fontSize="sm">Konu</FormLabel>
              <Input
                size="sm"
                value={newTicket.konu}
                onChange={(e) =>
                  setNewTicket((prev) => ({ ...prev, konu: e.target.value }))
                }
                placeholder="Örn: Cihaz bağlantı sorunu"
              />
            </FormControl>

            <FormControl mb={3} isRequired>
              <FormLabel fontSize="sm">Açıklama</FormLabel>
              <Textarea
                rows={4}
                size="sm"
                value={newTicket.aciklama}
                onChange={(e) =>
                  setNewTicket((prev) => ({ ...prev, aciklama: e.target.value }))
                }
                placeholder="Sorunun detayını yazın..."
              />
            </FormControl>

            <FormControl mb={3}>
              <FormLabel fontSize="sm">Öncelik</FormLabel>
              <Select
                size="sm"
                value={newTicket.oncelik}
                onChange={(e) =>
                  setNewTicket((prev) => ({ ...prev, oncelik: e.target.value }))
                }
              >
                <option value="dusuk">Düşük</option>
                <option value="normal">Normal</option>
                <option value="yuksek">Yüksek</option>
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel fontSize="sm">Atanan Kişi</FormLabel>
              <Input
                size="sm"
                value={newTicket.atanan_kisi}
                onChange={(e) =>
                  setNewTicket((prev) => ({
                    ...prev,
                    atanan_kisi: e.target.value,
                  }))
                }
                placeholder="Opsiyonel"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={createModal.onClose}>
              İptal
            </Button>
            <Button
              colorScheme="blue"
              isLoading={saving}
              onClick={async () => {
                if (!newTicket.device_id) {
                  toast({
                    title: "Araç seçimi gerekli",
                    status: "warning",
                  });
                  return;
                }
                if (!newTicket.konu.trim() || !newTicket.aciklama.trim()) {
                  toast({
                    title: "Konu ve açıklama zorunlu",
                    status: "warning",
                  });
                  return;
                }
                try {
                  setSaving(true);
                  const device = devices.find(
                    (d) => String(d.id) === String(newTicket.device_id)
                  );
                  const payload = {
                    device_id: device ? device.id : Number(newTicket.device_id),
                    plaka: device?.plaka || null,
                    firma_id: device?.firma_id || null,
                    firma_adi: device?.firma_adi || null,
                    telefon: device?.firma_telefon || null,
                    email: device?.firma_email || null,
                    konu: newTicket.konu.trim(),
                    aciklama: newTicket.aciklama.trim(),
                    oncelik: newTicket.oncelik || "normal",
                    atanan_kisi: newTicket.atanan_kisi || null,
                  };
                  const res = await api.post("/api/crm/satis-sonrasi", payload);
                  const data = await res.json();
                  if (!res.ok) {
                    throw new Error(
                      data?.detail || "Destek kaydı oluşturulamadı."
                    );
                  }
                  toast({
                    title: "Destek kaydı oluşturuldu",
                    status: "success",
                  });
                  setNewTicket({
                    device_id: "",
                    konu: "",
                    aciklama: "",
                    oncelik: "normal",
                    atanan_kisi: "",
                  });
                  createModal.onClose();
                  fetchItems();
                } catch (err) {
                  toast({
                    title: "Hata",
                    description: err.message,
                    status: "error",
                  });
                } finally {
                  setSaving(false);
                }
              }}
            >
              Kaydet
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Sağ tık menüsü */}
      <SatisSonrasiContextMenu
        position={contextMenuPos}
        data={selectedRow}
        onClose={closeContextMenu}
        onResolveOnly={handleResolveOnly}
        onSendSms={openSmsModal}
        onAddNote={openNoteModal}
      />

      {/* Çözüldü + SMS Modalı */}
      <Modal isOpen={resolveModal.isOpen} onClose={closeResolveModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Kayıt Çözüldü + SMS Gönder</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Telefon</FormLabel>
              <Input
                size="sm"
                value={resolveModal.telefon || ""}
                onChange={(e) =>
                  setResolveModal((prev) => ({
                    ...prev,
                    telefon: e.target.value,
                  }))
                }
                placeholder="05XXXXXXXXX"
              />
              <FormHelperText fontSize="xs" color="gray.500">
                Başında 0 olacak şekilde 11 hane girin (ör: 0532xxxxxxx).
              </FormHelperText>
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">SMS Mesajı</FormLabel>
              <Textarea
                rows={4}
                size="sm"
                placeholder="Müşteriye gidecek SMS metni..."
                value={resolveModal.sms_message}
                onChange={(e) =>
                  setResolveModal((prev) => ({
                    ...prev,
                    sms_message: e.target.value,
                  }))
                }
              />
              <FormHelperText fontSize="xs" color="gray.500">
                Boş bırakırsanız SMS gönderilmez, sadece kayıt çözüldü olarak
                işaretlenir.
              </FormHelperText>
            </FormControl>
            <FormControl>
              <FormLabel fontSize="sm">Çözüm Notu</FormLabel>
              <Textarea
                rows={3}
                size="sm"
                placeholder="Çözümle ilgili kısa not..."
                value={resolveModal.note}
                onChange={(e) =>
                  setResolveModal((prev) => ({
                    ...prev,
                    note: e.target.value,
                  }))
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeResolveModal}>
              İptal
            </Button>
            <Button colorScheme="green" onClick={handleResolveSave}>
              Çözüldü Olarak İşaretle
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* SMS Gönder Modalı */}
      <Modal isOpen={smsModal.isOpen} onClose={closeSmsModal} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>SMS Gönder</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">Telefon</FormLabel>
              <Input
                size="sm"
                value={smsModal.telefon || ""}
                onChange={(e) =>
                  setSmsModal((prev) => ({
                    ...prev,
                    telefon: e.target.value,
                  }))
                }
                placeholder="05XXXXXXXXX"
              />
              <FormHelperText fontSize="xs" color="gray.500">
                Başında 0 olacak şekilde 11 hane girin (ör: 0532xxxxxxx).
              </FormHelperText>
            </FormControl>
            <FormControl mb={3}>
              <FormLabel fontSize="sm">SMS Mesajı</FormLabel>
              <Textarea
                rows={8}
                size="md"
                placeholder="Müşteriye gidecek SMS metni..."
                value={smsModal.message}
                onChange={(e) =>
                  setSmsModal((prev) => ({
                    ...prev,
                    message: e.target.value,
                  }))
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeSmsModal}>
              İptal
            </Button>
            <Button colorScheme="teal" onClick={handleSmsSave}>
              SMS Gönder
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Not Ekle Modalı */}
      <Modal isOpen={noteModal.isOpen} onClose={closeNoteModal} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Not Ekle</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={4}>
            <FormControl>
              <FormLabel fontSize="sm">Not Metni</FormLabel>
              <Textarea
                rows={4}
                size="sm"
                placeholder="Not metnini yazın..."
                value={noteModal.not_metni}
                onChange={(e) =>
                  setNoteModal((prev) => ({
                    ...prev,
                    not_metni: e.target.value,
                  }))
                }
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeNoteModal}>
              İptal
            </Button>
            <Button colorScheme="blue" onClick={handleNoteSave}>
              Not Ekle
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

